"use client";

import type { PointerEvent } from "react";
import { canAnimatePointer, loadGsap } from "@/lib/gsap";

export function useBlogCardHoverAnimation() {
	async function handlePointerMove(event: PointerEvent<HTMLAnchorElement>) {
		if (!canAnimatePointer()) return;

		const card = event.currentTarget;
		const image = card.querySelector<HTMLElement>(".blog-card-image");
		const glow = card.querySelector<HTMLElement>(".blog-card-glow");
		const overlay = card.querySelector<HTMLElement>(".blog-card-overlay");
		const arrow = card.querySelector<HTMLElement>(".blog-card-arrow");
		const content = card.querySelector<HTMLElement>(".blog-card-content");

		const rect = card.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;
		const offsetX = (x / rect.width - 0.5) * 12;
		const offsetY = (y / rect.height - 0.5) * 10;
		const gsap = await loadGsap();

		gsap.to(card, {
			y: -10,
			scale: 1.012,
			boxShadow: "0 28px 60px rgba(0,0,0,0.28)",
			duration: 0.45,
			ease: "power3.out",
		});

		if (image) {
			gsap.to(image, {
				scale: 1.07,
				x: offsetX,
				y: offsetY,
				duration: 0.7,
				ease: "power3.out",
			});
		}

		if (glow) {
			gsap.to(glow, {
				x,
				y,
				opacity: 1,
				duration: 0.35,
				ease: "power3.out",
			});
		}

		if (overlay) {
			gsap.to(overlay, {
				opacity: 0.92,
				duration: 0.35,
				ease: "power2.out",
			});
		}

		if (arrow) {
			gsap.to(arrow, {
				x: 8,
				duration: 0.35,
				ease: "power3.out",
			});
		}

		if (content) {
			gsap.to(content, {
				y: -4,
				x: 4,
				duration: 0.4,
				ease: "power3.out",
			});
		}
	}

	async function handlePointerLeave(event: PointerEvent<HTMLAnchorElement>) {
		if (!canAnimatePointer()) return;

		const card = event.currentTarget;
		const image = card.querySelector<HTMLElement>(".blog-card-image");
		const glow = card.querySelector<HTMLElement>(".blog-card-glow");
		const overlay = card.querySelector<HTMLElement>(".blog-card-overlay");
		const arrow = card.querySelector<HTMLElement>(".blog-card-arrow");
		const content = card.querySelector<HTMLElement>(".blog-card-content");
		const gsap = await loadGsap();

		gsap.to(card, {
			y: 0,
			scale: 1,
			boxShadow: "0 0px 0px rgba(0,0,0,0)",
			duration: 0.55,
			ease: "power3.out",
		});

		if (image) {
			gsap.to(image, {
				scale: 1,
				x: 0,
				y: 0,
				duration: 0.75,
				ease: "power3.out",
			});
		}

		if (glow) {
			gsap.to(glow, {
				opacity: 0,
				duration: 0.35,
				ease: "power2.out",
			});
		}

		if (overlay) {
			gsap.to(overlay, {
				opacity: 0,
				duration: 0.35,
				ease: "power2.out",
			});
		}

		if (arrow) {
			gsap.to(arrow, {
				x: 0,
				duration: 0.35,
				ease: "power3.out",
			});
		}

		if (content) {
			gsap.to(content, {
				y: 0,
				x: 0,
				duration: 0.4,
				ease: "power3.out",
			});
		}
	}

	return {
		handlePointerMove,
		handlePointerLeave,
	};
}
